import React, { useState, useMemo } from 'react';
import { Calendar, FileText, Mail, Eye, Check, XCircle, AlertCircle, Clock, ChevronLeft, Settings, CheckCircle } from 'lucide-react';
import { StatusPill, FileTypeBadge, formatTimestamp, formatDate } from './FileTrackingShared';

// Get display status from record — trust the backend status.
const getDisplayStatus = (record) => {
  if (!record) return 'pending';
  const statusMap = {
    needs_review: 'needs_review',
    processed: 'processed',
    received: 'pending',
    pending: 'pending',
    missed: 'missed',
    failed: 'failed'
  };
  return statusMap[record.status] || 'pending';
};

// Compact Stat Item for header
const StatItem = ({ icon: Icon, iconColor, count, label }) => (
  <div className="flex items-center gap-1.5">
    <Icon size={14} className={iconColor} strokeWidth={2.5} />
    <span className="text-sm font-semibold text-gray-900">{count}</span>
    <span className="cp-help">{label}</span>
  </div>
);

// File Item Row
const FileItem = ({ record, onEmail, onViewFile, onClick, isSelected }) => {
  const displayStatus = getDisplayStatus(record);
  const locationName = record.metadata?.locationName || `Hotel ${record.hotelId}`;
  const fileType = record.displayName || record.fileType || 'File';

  return (
    <div
      onClick={onClick}
      className={`flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 -mx-4 px-4 transition-colors ${isSelected ? 'bg-orange-50' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-gray-800 truncate">{locationName}</span>
          <FileTypeBadge fileType={fileType} />
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span>{formatTimestamp(record.reception?.receivedAt) || 'Not received'}</span>
          <StatusPill status={displayStatus} size="small" />
        </div>
      </div>

      <div className="flex items-center gap-1.5 ml-3" onClick={e => e.stopPropagation()}>
        {onEmail && (
          <button
            onClick={() => onEmail(record)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-orange-500 transition-colors"
            title="Send email"
          >
            <Mail size={16} strokeWidth={2} />
          </button>
        )}
        {(record.reception?.blobUrl || record.tracking?.lastProcessedFile) && onViewFile && (
          <button
            onClick={() => onViewFile(record)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-blue-500 transition-colors"
            title="View file"
          >
            <Eye size={16} strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
};

// Expanded File Detail View
const ExpandedFileDetail = ({ record, onBack, onEmail, onViewFile, onConfigure, onClearReview }) => {
  const displayStatus = getDisplayStatus(record);
  const locationName = record.metadata?.locationName || `Hotel ${record.hotelId}`;
  const fileType = record.displayName || record.fileType || 'File';

  // Build timeline events
  const timelineEvents = [];
  if (record.reception?.receivedAt) {
    timelineEvents.push({ label: 'File Received', time: record.reception.receivedAt, complete: true });
  }
  if (record.processing?.startedAt || record.reception?.receivedAt) {
    timelineEvents.push({ label: 'Processing Started', time: record.processing?.startedAt || record.reception?.receivedAt, complete: record.status === 'processed' });
  }
  if (record.processing?.completedAt || record.status === 'processed') {
    timelineEvents.push({ label: 'Processing Complete', time: record.processing?.completedAt || record.tracking?.lastSuccessfullyProcessed, complete: true });
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Back button header */}
      <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ChevronLeft size={16} />
          <span>Back to files</span>
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Header with status */}
        <div className="p-4 bg-gradient-to-br from-orange-50 to-white border-b border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <StatusPill status={displayStatus} />
            {onEmail && (
              <button
                onClick={() => onEmail(record)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Mail size={14} />
                Email
              </button>
            )}
            {displayStatus === 'needs_review' && onClearReview && (
              <button
                onClick={() => onClearReview(record)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 rounded-lg text-xs font-medium text-white hover:bg-green-600 transition-colors"
              >
                <Check size={14} />
                Mark as Reviewed
              </button>
            )}
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-1">{locationName}</h2>
          <p className="text-sm text-gray-500 font-mono">
            ID: {record.hotelId} · {fileType}
          </p>
        </div>

        {/* File Details */}
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
            File Details
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-400 mb-0.5">Effective Date</div>
              <div className="cp-name">
                {formatDate(record.effectiveDate) || '–'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-0.5">Rows Processed</div>
              <div className="cp-name">
                {record.processing?.rowsInserted || record.tracking?.lastRowsProcessed || '–'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-0.5">Source</div>
              <div className="cp-name">
                {record.reception?.receivedFrom || 'Email'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-0.5">Assigned To</div>
              <div className="cp-name">
                {record.tracking?.assignedTo || '–'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-0.5">Alerts</div>
              <div className="cp-name">
                {record.tracking?.alertsEnabled ? 'Enabled' : 'None'}
              </div>
            </div>
          </div>
        </div>

        {/* Processing Timeline */}
        {timelineEvents.length > 0 && (
          <div className="p-4">
            <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Processing Timeline
            </h3>
            <div className="space-y-0">
              {timelineEvents.map((event, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${event.complete ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <CheckCircle size={14} className={event.complete ? 'text-green-600' : 'text-gray-400'} />
                    </div>
                    {index < timelineEvents.length - 1 && (
                      <div className="w-0.5 h-8 bg-gray-200 my-1" />
                    )}
                  </div>
                  <div className="pb-4">
                    <div className="cp-name">{event.label}</div>
                    <div className="cp-help">
                      {event.time ? formatTimestamp(event.time) : '–'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="p-4 border-t border-gray-200 flex-shrink-0">
        <div className="flex gap-3">
          {(record.reception?.blobUrl || record.tracking?.lastProcessedFile) && onViewFile && (
            <button
              onClick={() => onViewFile(record)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Eye size={16} />
              View File
            </button>
          )}
          {onConfigure && (
            <button
              onClick={() => onConfigure(record)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Settings size={16} />
              Configure Alerts
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const HistoricalDetailPanel = ({
  selectedDate,
  records = [],
  onEmailRecord,
  onViewFile,
  onClearReview,
  onConfigure,
  currentUser,
  onBack
}) => {
  const [selectedFile, setSelectedFile] = useState(null);

  // Filter records for the selected date
  const dayRecords = useMemo(() => {
    if (!selectedDate) return [];
    return records.filter(r => r.effectiveDate === selectedDate);
  }, [selectedDate, records]);

  // Reset selected file when date changes
  React.useEffect(() => {
    setSelectedFile(null);
  }, [selectedDate]);

  // Sync selected file with refreshed records (use ref to avoid infinite loop)
  const selectedFileIdRef = React.useRef(null);
  React.useEffect(() => {
    if (selectedFile) {
      selectedFileIdRef.current = selectedFile.id;
    }
  }, [selectedFile]);

  React.useEffect(() => {
    if (selectedFileIdRef.current && dayRecords.length > 0) {
      const updatedRecord = dayRecords.find(r => r.id === selectedFileIdRef.current);
      if (updatedRecord) {
        setSelectedFile(updatedRecord);
      }
    }
  }, [dayRecords]);

  // Calculate stats for the day
  const dayStats = useMemo(() => {
    const stats = {
      processed: 0,
      missed: 0,
      needsReview: 0,
      pending: 0
    };

    dayRecords.forEach(record => {
      if (record.status === 'needs_review') {
        stats.needsReview++;
      } else if (record.status === 'processed') {
        stats.processed++;
      } else if (record.status === 'missed' || record.status === 'failed') {
        stats.missed++;
      } else {
        stats.pending++;
      }
    });

    return stats;
  }, [dayRecords]);

  const formattedDate = formatDate(selectedDate);

  // Empty state - no date selected
  if (!selectedDate) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
            <Calendar size={24} className="text-gray-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Select a date</h3>
          <p className="cp-help">Click on a day in the calendar to view files</p>
        </div>
      </div>
    );
  }

  // Show expanded file detail view
  if (selectedFile) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col h-full max-h-full">
        <ExpandedFileDetail
          record={selectedFile}
          onBack={() => setSelectedFile(null)}
          onEmail={onEmailRecord}
          onViewFile={onViewFile}
          onConfigure={onConfigure}
          onClearReview={onClearReview}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col h-full max-h-full">
      {/* Header with date icon and stats */}
      <div className="p-4 border-b border-gray-200 bg-gradient-to-br from-orange-50 to-white flex-shrink-0">
        {/* Mobile back button */}
        {onBack && (
          <button
            onClick={onBack}
            className="md:hidden flex items-center gap-1 text-[13px] text-gray-500 mb-2 -ml-1"
          >
            <ChevronLeft size={16} />
            Back to calendar
          </button>
        )}
        <div className="flex items-center gap-3">
          {/* Date Icon Card */}
          <div className="w-12 h-12 bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center shadow-sm flex-shrink-0">
            <span className="text-[9px] font-bold text-orange-500 uppercase">
              {selectedDate ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' }) : ''}
            </span>
            <span className="text-lg font-bold text-gray-900">
              {selectedDate ? new Date(selectedDate + 'T00:00:00').getDate() : ''}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-gray-900">{formattedDate}</h2>
            <span className="cp-help">
              {dayRecords.length} file{dayRecords.length !== 1 ? 's' : ''} tracked
            </span>
          </div>
        </div>

        {/* Inline Stats */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200/50">
          <StatItem icon={Check} iconColor="text-green-600" count={dayStats.processed} label="Processed" />
          <StatItem icon={XCircle} iconColor="text-red-600" count={dayStats.missed} label="Missed" />
          <StatItem icon={AlertCircle} iconColor="text-blue-600" count={dayStats.needsReview} label="Review" />
          <StatItem icon={Clock} iconColor="text-yellow-600" count={dayStats.pending} label="Pending" />
        </div>
      </div>

      {/* Files List */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <div className="px-4 pt-4 pb-2 flex-shrink-0">
          <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
            Files for this date
          </h3>
        </div>

        {dayRecords.length === 0 ? (
          <div className="text-center py-6 flex-1">
            <FileText size={28} className="mx-auto text-gray-300 mb-2" />
            <p className="cp-help">No files tracked for this date</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            {dayRecords.map((record, index) => (
              <FileItem
                key={record.id || `${record.hotelId}-${record.fileType}-${index}`}
                record={record}
                onEmail={onEmailRecord}
                onViewFile={onViewFile}
                onClick={() => setSelectedFile(record)}
                isSelected={selectedFile?.id === record.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Quick actions when there are records needing attention */}
      {(dayStats.missed > 0 || dayStats.needsReview > 0) && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={() => {
              // Send email for all problem files
              const problemRecords = dayRecords.filter(r =>
                r.status === 'missed' ||
                r.status === 'failed' ||
                r.status === 'needs_review'
              );
              if (problemRecords.length > 0 && onEmailRecord) {
                onEmailRecord(problemRecords[0]); // Email modal will handle multiple
              }
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 rounded-lg text-xs font-medium text-white hover:bg-orange-600 transition-colors"
          >
            <Mail size={14} strokeWidth={2} />
            Email About {dayStats.missed + dayStats.needsReview} Issue{(dayStats.missed + dayStats.needsReview) !== 1 ? 's' : ''}
          </button>
        </div>
      )}
    </div>
  );
};

export default HistoricalDetailPanel;
